// Single source of truth for pricing tiers — used by the Pricing page and the Landing teaser.
// Annual billing = "2 months free" (pay for 10 months), shown as a per-month equivalent.
export const ANNUAL_MONTHS = 10;

export const TIERS = [
  {
    key: "free",
    name: "Free",
    monthly: 0,
    who: "For exploration",
    features: ["Limited AI research", "1 knowledge project", "Basic document uploads", "Limited exports"],
    cta: "Start free",
    to: "/register",
  },
  {
    key: "student",
    name: "Student",
    monthly: 5,
    who: "For students",
    features: [
      "5 knowledge projects",
      "Educational modules",
      "Quizzes & flashcards",
      "PDF exports",
      "Monthly AI credit allowance",
    ],
    cta: "Get started",
    to: "/register",
  },
  {
    key: "creator",
    name: "Creator",
    monthly: 15,
    who: "For independent creators",
    features: [
      "20 knowledge projects",
      "Book & course creation",
      "Structured knowledge databases",
      "CSV, Markdown & PDF exports",
      "Higher AI credit allowance",
    ],
    cta: "Get started",
    to: "/register",
  },
  {
    key: "professional",
    name: "Professional",
    monthly: 29,
    who: "For researchers & consultants",
    popular: true,
    features: [
      "50 knowledge projects",
      "Advanced research workflows",
      "Knowledge graphs",
      "Citation management",
      "API access with usage limits",
    ],
    cta: "Get started",
    to: "/register",
  },
  {
    key: "business",
    name: "Business",
    monthly: 79,
    who: "For small organizations",
    features: [
      "5 team members",
      "Shared knowledge repositories",
      "Role-based access",
      "Collaborative editing & version history",
      "Higher usage allowances",
    ],
    cta: "Get started",
    to: "/register",
  },
  {
    key: "enterprise",
    name: "Enterprise",
    monthly: null, // custom
    who: "For universities & corporations",
    features: [
      "Private deployment options",
      "SSO & advanced permissions",
      "Custom integrations",
      "Data governance",
      "Dedicated support & SLAs",
    ],
    cta: "Contact sales",
    contact: true,
  },
];

// Price label for a tier given the billing period ("monthly" | "annual").
export function priceLabel(tier, period) {
  if (tier.monthly == null) return { amount: "Custom", sub: "" };
  if (tier.monthly === 0) return { amount: "$0", sub: "/mo" };
  if (period === "annual") {
    const perMonth = Math.round((tier.monthly * ANNUAL_MONTHS) / 12 * 100) / 100;
    return { amount: `$${perMonth}`, sub: "/mo", note: `billed annually ($${tier.monthly * ANNUAL_MONTHS}/yr)` };
  }
  return { amount: `$${tier.monthly}`, sub: "/mo" };
}

// Feature comparison matrix — rendered as the "Compare all features" table under the tiers.
// Columns map to TIER keys. A value can be true (check), false (dash), or a string.
export const COMPARE_HIGHLIGHT = ["professional", "business"]; // visually nudged columns
export const COMPARE_COLUMNS = ["free", "student", "creator", "professional", "business", "enterprise"];
export const FEATURE_GROUPS = [
  {
    group: "Knowledge projects",
    rows: [
      { label: "Knowledge projects", free: "1", student: "5", creator: "20", professional: "50", business: "Unlimited", enterprise: "Unlimited" },
      { label: "Team members", free: "1", student: "1", creator: "1", professional: "3", business: "5", enterprise: "Custom" },
      { label: "Monthly AI credit allowance", free: "Trial", student: "Basic", creator: "Higher", professional: "Advanced", business: "High-volume", enterprise: "Custom" },
    ],
  },
  {
    group: "Creation & processing",
    rows: [
      { label: "AI research & structuring", free: "Limited", student: true, creator: true, professional: true, business: true, enterprise: true },
      { label: "Book & course creation", free: false, student: false, creator: true, professional: true, business: true, enterprise: true },
      { label: "Structured knowledge databases", free: false, student: false, creator: true, professional: true, business: true, enterprise: true },
      { label: "Advanced research workflows", free: false, student: false, creator: false, professional: true, business: true, enterprise: true },
      { label: "Knowledge graphs", free: false, student: false, creator: false, professional: true, business: true, enterprise: true },
      { label: "Citation management", free: false, student: false, creator: false, professional: true, business: true, enterprise: true },
    ],
  },
  {
    group: "Exports & outputs",
    rows: [
      { label: "PDF exports", free: "Limited", student: true, creator: true, professional: true, business: true, enterprise: true },
      { label: "CSV & Markdown exports", free: false, student: false, creator: true, professional: true, business: true, enterprise: true },
      { label: "Large batch processing", free: false, student: false, creator: false, professional: true, business: true, enterprise: true },
    ],
  },
  {
    group: "Collaboration & scale",
    rows: [
      { label: "Shared knowledge repositories", free: false, student: false, creator: false, professional: false, business: true, enterprise: true },
      { label: "Role-based access", free: false, student: false, creator: false, professional: false, business: true, enterprise: true },
      { label: "Version history", free: false, student: false, creator: false, professional: false, business: true, enterprise: true },
      { label: "API access", free: false, student: false, creator: false, professional: "With limits", business: "Higher limits", enterprise: "Custom" },
    ],
  },
  {
    group: "Enterprise & governance",
    rows: [
      { label: "SSO & advanced permissions", free: false, student: false, creator: false, professional: false, business: false, enterprise: true },
      { label: "Private deployment options", free: false, student: false, creator: false, professional: false, business: false, enterprise: true },
      { label: "Custom integrations", free: false, student: false, creator: false, professional: false, business: false, enterprise: true },
      { label: "Data governance", free: false, student: false, creator: false, professional: false, business: false, enterprise: true },
      { label: "Dedicated support & SLAs", free: "Community", student: "Community", creator: "Email", professional: "Priority", business: "Priority", enterprise: "Dedicated" },
    ],
  },
];

export const ADDONS = [
  { icon: "Coins", title: "AI Credits", desc: "Pay-as-you-go credits for intensive research and large processing runs." },
  { icon: "Store", title: "Knowledge Marketplace", desc: "Publish and sell knowledge products. Creators keep ownership; 15% commission." },
  { icon: "Plug", title: "API Access", desc: "Usage-based billing to pipe Espresso into your own tools and workflows." },
  { icon: "Building2", title: "Enterprise Licensing", desc: "Annual organizational contracts with governance and support." },
  { icon: "GraduationCap", title: "Educational Licensing", desc: "Institution-wide subscriptions for schools and universities." },
  { icon: "Wrench", title: "Custom Knowledge Engineering", desc: "Project-based builds — we structure and process your corpus for you." },
];

export const FAQS = [
  {
    q: "What exactly is a knowledge project?",
    a: "A project is one body of knowledge — a book, course, research compendium, manual or knowledge base. You ingest a prompt, an index, or a CSV, Espresso structures it into modules and documents, then processes it into finished, publication-ready output.",
  },
  {
    q: "How do AI credits work?",
    a: "Each plan includes a monthly AI allowance for generation. Running very large jobs (thousands of documents) can use additional pay-as-you-go credits, so you only pay for the heavy processing you actually run.",
  },
  {
    q: "Can I change or cancel my plan anytime?",
    a: "Yes. You can upgrade, downgrade or cancel at any time — changes take effect from your next billing cycle, and your knowledge projects stay intact.",
  },
  {
    q: "What do I save with annual billing?",
    a: "Annual plans are billed once a year and give you two months free versus paying monthly — the per-month price you see on the annual toggle already reflects that discount.",
  },
  {
    q: "Do I own what I create?",
    a: "Always. You retain ownership of your original intellectual property. If you choose to publish on the Knowledge Marketplace, you keep your rights and set your own licensing.",
  },
  {
    q: "What makes Enterprise different?",
    a: "Enterprise adds private deployment options, SSO and advanced permissions, custom integrations, data governance, dedicated support and contractual service levels — built for universities and corporations.",
  },
];
